import { apiClient } from "@/api/client";
import { API_ENDPOINTS } from "@/config/app";
import type { PaginatedResponse, PaginationParams } from "@/types/api";
import { buildUrl } from "@/utils/buildUrl";

import type { GroupId } from "../groups";
import type { OptionItemWithChildren } from "../types";
import type {
  BrandId,
  BrandMenuFull,
  BrandMenuGet,
  BrandMenuParseResponse,
  BrandMenuPatch,
  BrandMenuPost,
  IBrand,
  IBrandGetById,
  IBrandInfo,
  IBrandPatchPayload,
  MenuCategoriesReorderPost,
  MenuCategoryCreatePost,
  MenuCategoryPatch,
  MenuDishCreatePost,
  MenuDishesReorderPost,
  MenuDishGet,
  MenuDishPatch,
} from "./types";

const hasMenu = (data: BrandMenuGet | null | undefined): data is BrandMenuGet =>
  Boolean(data?.id);

export const brandsApi = {
  async postNewBrand(brandData: IBrand) {
    const response = await apiClient.post<Omit<IBrand, "id">>(
      `${API_ENDPOINTS.ADMIN}/brands`,
      brandData
    );
    return response;
  },

  async getAllBrands(
    params?: PaginationParams
  ): Promise<PaginatedResponse<IBrandInfo>> {
    const url = buildUrl(API_ENDPOINTS.ADMIN, "/brands", params);
    const response = await apiClient.get<PaginatedResponse<IBrandInfo>>(url);
    return response;
  },

  async patchBrand(brandId: BrandId, brandData: IBrandPatchPayload) {
    const response = await apiClient.patch(
      `${API_ENDPOINTS.ADMIN}/brands/${brandId}`,
      brandData
    );
    return response;
  },

  async deleteBrand(brandId: BrandId) {
    const response = await apiClient.delete(
      `${API_ENDPOINTS.ADMIN}/brands/${brandId}`
    );
    return response;
  },

  async getBrandById(brandId: BrandId): Promise<IBrandGetById> {
    const response = await apiClient.get<IBrandGetById>(
      `${API_ENDPOINTS.ADMIN}/brands/${brandId}`
    );
    return response;
  },

  async getBrandAvailableLegalEntityByGroupId(
    groupId: GroupId
  ): Promise<OptionItemWithChildren[]> {
    const response = await apiClient.get<OptionItemWithChildren[]>(
      `${API_ENDPOINTS.ADMIN}/brands/available-legal-entities/${groupId}`
    );
    return response;
  },

  async getMenu(brandId: BrandId): Promise<BrandMenuGet | null> {
    const response = await apiClient.get<BrandMenuGet>(
      `${API_ENDPOINTS.ADMIN}/brands/${brandId}/menu`
    );

    return hasMenu(response) ? response : null;
  },

  async createMenu(
    brandId: BrandId,
    menu?: BrandMenuPost
  ): Promise<BrandMenuGet> {
    const response = await apiClient.post<BrandMenuGet>(
      `${API_ENDPOINTS.ADMIN}/brands/${brandId}/menu`,
      menu
    );
    return response;
  },

  async patchMenu(
    brandId: BrandId,
    menu: BrandMenuPatch
  ): Promise<BrandMenuGet> {
    const response = await apiClient.patch<BrandMenuGet>(
      `${API_ENDPOINTS.ADMIN}/brands/${brandId}/menu`,
      menu
    );
    return response;
  },

  async postFullMenu(
    brandId: BrandId,
    menu: BrandMenuFull
  ): Promise<BrandMenuGet> {
    const response = await apiClient.post<BrandMenuGet>(
      `${API_ENDPOINTS.ADMIN}/brands/${brandId}/menu/full`,
      menu
    );
    return response;
  },

  async createMenuCategory(
    brandId: BrandId,
    payload: MenuCategoryCreatePost
  ): Promise<BrandMenuGet> {
    return apiClient.post<BrandMenuGet>(
      `${API_ENDPOINTS.ADMIN}/brands/${brandId}/menu/categories`,
      payload
    );
  },

  async updateMenuCategory(
    brandId: BrandId,
    menuCategoryId: string,
    payload: MenuCategoryPatch
  ): Promise<BrandMenuGet> {
    return apiClient.patch<BrandMenuGet>(
      `${API_ENDPOINTS.ADMIN}/brands/${brandId}/menu/categories/${menuCategoryId}`,
      payload
    );
  },

  async reorderMenuCategories(
    brandId: BrandId,
    payload: MenuCategoriesReorderPost
  ): Promise<void> {
    await apiClient.patch(
      `${API_ENDPOINTS.ADMIN}/brands/${brandId}/menu/categories/reorder`,
      payload
    );
  },

  async deleteMenuCategory(
    brandId: BrandId,
    menuCategoryId: string
  ): Promise<void> {
    await apiClient.delete(
      `${API_ENDPOINTS.ADMIN}/brands/${brandId}/menu/categories/${menuCategoryId}`
    );
  },

  async createMenuDish(
    brandId: BrandId,
    menuCategoryId: string,
    payload: MenuDishCreatePost
  ): Promise<MenuDishGet> {
    return apiClient.post<MenuDishGet>(
      `${API_ENDPOINTS.ADMIN}/brands/${brandId}/menu/categories/${menuCategoryId}/dishes`,
      payload
    );
  },

  async reorderMenuDishes(
    brandId: BrandId,
    menuCategoryId: string,
    payload: MenuDishesReorderPost
  ): Promise<void> {
    await apiClient.patch(
      `${API_ENDPOINTS.ADMIN}/brands/${brandId}/menu/categories/${menuCategoryId}/dishes/reorder`,
      payload
    );
  },

  async getMenuDish(brandId: BrandId, dishId: string): Promise<MenuDishGet> {
    return apiClient.get<MenuDishGet>(
      `${API_ENDPOINTS.ADMIN}/brands/${brandId}/menu/dishes/${dishId}`
    );
  },

  async updateMenuDish(
    brandId: BrandId,
    dishId: string,
    payload: MenuDishPatch
  ): Promise<MenuDishGet> {
    return apiClient.patch<MenuDishGet>(
      `${API_ENDPOINTS.ADMIN}/brands/${brandId}/menu/dishes/${dishId}`,
      payload
    );
  },

  async deleteMenuDish(brandId: BrandId, dishId: string): Promise<void> {
    await apiClient.delete(
      `${API_ENDPOINTS.ADMIN}/brands/${brandId}/menu/dishes/${dishId}`
    );
  },

  async deleteMenu(brandId: BrandId): Promise<void> {
    await apiClient.delete(`${API_ENDPOINTS.ADMIN}/brands/${brandId}/menu`);
  },

  async resetMenu(brandId: BrandId): Promise<BrandMenuGet> {
    await this.deleteMenu(brandId);
    return this.createMenu(brandId);
  },

  async parseMenuFromUrl(url: string): Promise<BrandMenuParseResponse> {
    return apiClient.post<BrandMenuParseResponse>(
      `${API_ENDPOINTS.ADMIN}/menu/parse/url`,
      { url }
    );
  },

  async parseMenuFromFile(file: File | Blob): Promise<BrandMenuParseResponse> {
    const formData = new FormData();
    formData.append("file", file);

    return apiClient.postForm<BrandMenuParseResponse>(
      `${API_ENDPOINTS.ADMIN}/menu/parse/file`,
      formData
    );
  },
};
